import React, { useEffect, useState, useCallback } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { B200, B300, N40 } from '@atlaskit/theme/colors';
import CheckboxIcon from '@atlaskit/icon/glyph/checkbox';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, xcss } from '@atlaskit/primitives';

import { useIsDisabledContext } from '../IsDisabledContext';
import type { UnsplashResult } from '../index';

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
	paddingRight: 'space.025',
	paddingLeft: 'space.025',
});

const baseImageStyles = {
	height: '44px',
	width: '46px',
	borderRadius: '4px',
	border: 'none',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImagePlaceholder = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	backgroundColor: token('color.background.neutral', N40),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.button<{
	imageSrc: string;
	isSelectedImage: boolean;
	isUploadingImage?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: `${({ isUploadingImage }) => (isUploadingImage ? 'auto' : 'pointer')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundImage: `${({ imageSrc }) => `url(${imageSrc})`}`,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	position: 'relative',
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'flex-end',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: `${({ isSelectedImage }) =>
		isSelectedImage ? `inset 0 0 0 2px ${token('color.border.selected', B300)}` : 'none'}`,

	'&:focus-visible': {
		outline: '2px solid',
		outlineColor: token('color.border.focused', B200),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		position: 'absolute',
		bottom: token('space.negative.075', '-6px'),
		right: token('space.negative.075', '-6px'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span > svg': {
		width: '30px',
		height: '30px',
	},
});

export const renderImagePlaceholders = () => {
	return Array(5)
		.fill(null)
		.map((_, index) => <ImagePlaceholder key={index} data-testid="image-picker-placeholders" />);
};

type DefaultImagePicker = {
	imageAltText?: string;
	selectedImageUrl?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	handleImageSelection: (selectedImage: UnsplashResult) => void;
	analyticsSource: string;
	isUploadingImage?: boolean;
	enablePaddingBottom?: boolean;
};

export const DefaultImagePicker = ({
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
}: DefaultImagePicker) => {
	const [isLoading, setIsLoading] = useState<boolean>(!initialUnsplashResults?.length);
	const [defaultUnsplashImages, setDefaultUnsplashImages] = useState<UnsplashResult[]>(
		initialUnsplashResults || [],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isDisabled = useIsDisabledContext();

	const onImageClick = (unsplashResult) => {
		if (!isUploadingImage) {
			handleDefaultImageSelection(unsplashResult);
		} else {
			return undefined;
		}
	};

	const reorderImages = useCallback(
		(results: any) => {
			if (
				initialUnsplashResults &&
				selectedImageUrl === initialUnsplashResults[0].fullSizeImageUrl
			) {
				return results;
			}

			const filteredState = results.filter(
				(image: UnsplashResult) => image.fullSizeImageUrl !== selectedImageUrl,
			);
			const selectedState = results.filter(
				(image: UnsplashResult) => image.fullSizeImageUrl === selectedImageUrl,
			);
			return selectedImageUrl
				? [
						{
							...selectedState,
							fullSizeImageUrl: selectedImageUrl,
							altDescription: imageAltText,
						},
						...filteredState.slice(1),
					]
				: filteredState;
		},
		[imageAltText, selectedImageUrl, initialUnsplashResults],
	);

	const truncateToMaxSize = (images: UnsplashResult[]) => {
		return images.length > 5 ? images.slice(0, 5) : images;
	};

	const handleDefaultImageSelection = (unsplashResult: UnsplashResult) => {
		handleImageSelection(unsplashResult);

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'selected',
				actionSubject: 'image',
				source: analyticsSource,
				attributes: {
					imageSource: 'unsplashDefault',
				},
			},
		}).fire();
	};

	useEffect(() => {
		if (!initialUnsplashResults?.length) return;

		if (
			defaultUnsplashImages.slice(0, 5).find((image) => image.fullSizeImageUrl === selectedImageUrl)
		) {
			setDefaultUnsplashImages(truncateToMaxSize(initialUnsplashResults));
		} else {
			setDefaultUnsplashImages(truncateToMaxSize(reorderImages(initialUnsplashResults)));
		}

		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialUnsplashResults, selectedImageUrl]);

	const renderImageButton = (unsplashResult: UnsplashResult) => {
		const { fullSizeImageUrl, altDescription, smallSizeImageUrl } = unsplashResult;
		const isSelectedImage = fullSizeImageUrl === selectedImageUrl;

		return (
			<Image
				key={unsplashResult.id}
				// default images have a thumbnail property, everything else does not
				imageSrc={smallSizeImageUrl ?? fullSizeImageUrl}
				isSelectedImage={isSelectedImage}
				aria-label={altDescription}
				isUploadingImage={isUploadingImage}
				onClick={() => onImageClick(unsplashResult)}
			>
				{isSelectedImage && (
					<CheckboxIcon label="checkmark" primaryColor={token('color.icon.brand', B300)} />
				)}
			</Image>
		);
	};

	return (
		<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
			{isLoading || isDisabled
				? renderImagePlaceholders()
				: defaultUnsplashImages.map(renderImageButton)}
		</Flex>
	);
};
